import type { Material } from '@vrlearning/lesson-schema';

/**
 * How each of a piece's materials looks (ADR-037, spec "Drawing"): `plain` is the light grey a
 * piece starts in; the woods are a code grain times their colour; metal shines with the sky's light.
 * Shared by the pieces (pieces.ts) and the lectern's swatches (furnish-lectern.ts), so a swatch is
 * the colour the piece becomes.
 */
export type MaterialLook = {
  readonly colour: number;
  readonly roughness: number;
  readonly metalness: number;
  readonly grain: boolean;
};

export const MATERIAL_LOOKS: Readonly<Record<Material, MaterialLook>> = {
  plain: { colour: 0xc9c9c9, roughness: 0.8, metalness: 0, grain: false },
  'light-wood': { colour: 0xd8b98a, roughness: 0.7, metalness: 0, grain: true },
  'dark-wood': { colour: 0x6b4a2f, roughness: 0.7, metalness: 0, grain: true },
  white: { colour: 0xf2f2f0, roughness: 0.6, metalness: 0, grain: false },
  black: { colour: 0x2a2a2a, roughness: 0.6, metalness: 0, grain: false },
  red: { colour: 0xc0392b, roughness: 0.6, metalness: 0, grain: false },
  blue: { colour: 0x2e6fb7, roughness: 0.6, metalness: 0, grain: false },
  green: { colour: 0x3f8f4f, roughness: 0.6, metalness: 0, grain: false },
  metal: { colour: 0xb8bcc2, roughness: 0.35, metalness: 0.9, grain: false },
};

/** The CSS colour of a material, for a swatch or a floor plan. */
export const cssColour = (material: Material): string =>
  `#${MATERIAL_LOOKS[material].colour.toString(16).padStart(6, '0')}`;
